import React, { useState, useEffect } from 'react';
import styled from "styled-components"
import { Link } from "react-router-dom"
import { Hash, Loader } from "lucide-react"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.textPrimary};
`

const TagsContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Tag = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.hoverBackground};
  color: ${props => props.theme.textPrimary};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  border: 1px solid ${props => props.theme.border};
  
  &:hover {
    background-color: ${props => props.theme.buttonSecondary};
    color: ${props => props.theme.textPrimary};
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  .count {
    margin-left: 6px;
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 10px;
  }
`

const LoadingContainer = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.textSecondary};
`

const ErrorMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: ${props => props.theme.error};
  font-size: 14px;
`

export default function PopularTags() {
  const { theme } = useTheme()
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPopularTags();
  }, []);

  const fetchPopularTags = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/post/popular-tags?limit=10', {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success && data.tags) {
        setTags(data.tags);
      } else {
        // API에서 태그가 없으면 샘플 데이터 사용
        setTags([
          { tag_name: "개발", usage_count: 45 },
          { tag_name: "디자인", usage_count: 32 },
          { tag_name: "프로젝트", usage_count: 28 },
          { tag_name: "앤츠톡", usage_count: 21 },
          { tag_name: "협업", usage_count: 18 },
        ]);
      }
    } catch (error) {
      console.error('인기 태그 로드 오류:', error);
      setError('태그를 불러오는 중 오류가 발생했습니다.');
      // 오류 시에도 샘플 데이터 표시
      setTags([
        { tag_name: "개발", usage_count: 45 },
        { tag_name: "디자인", usage_count: 32 },
        { tag_name: "프로젝트", usage_count: 28 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <Header theme={theme}>
          <Title theme={theme}>
            <Hash size={18} color={theme.textPrimary} />
            인기 태그
          </Title>
        </Header>
        <LoadingContainer theme={theme}>
          <Loader className="animate-spin" size={16} />
          <span style={{ marginLeft: '8px' }}>로딩 중...</span>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          <Hash size={18} color={theme.textPrimary} />
          인기 태그
        </Title>
      </Header>
      {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
      <TagsContainer>
        {tags.map((tag, index) => (
          <Tag key={index} theme={theme} to={`/tags/${tag.tag_name}`}>
            #{tag.tag_name}
            <span className="count">
              {parseInt(tag.usage_count) > 999 
                ? `${(parseInt(tag.usage_count) / 1000).toFixed(1)}k` 
                : tag.usage_count}
            </span>
          </Tag>
        ))}
      </TagsContainer>
    </Container>
  );
}

